import { useEffect, useState } from 'react';
import { Center, Spinner } from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'redux/store';
import { setIsAdmin } from 'redux/slices/userDataSlice';
import { getAccountDetails } from 'api/GoogleAuth';
import NotFoundPage from 'pages/NotFoundPage';

/**
 * Gates the admin screens on the admin role the server reports.
 *
 * Before this, the admin routes tested `isUserLoggedIn` alone, so any
 * authenticated customer who knew the address reached the admin surface. The
 * panel lives at a non-obvious path, which `router/adminPath.ts` itself
 * documents as "obscurity, not access control".
 *
 * 🔴 This is convenience, never the boundary. Every admin action is
 * re-authorised server-side by `get_admin_user` on each request. A user who
 * forges the flag sees an empty shell whose every call is rejected — that is
 * the intended failure mode, not a hole.
 *
 * Why it re-fetches instead of trusting what is already in the store: the site
 * reads the account exactly once, at page load, and only when the visitor is
 * not already signed in (`useAuthInitialization` runs `initializeAuth()` inside
 * `if (!isUserLoggedIn)`). Nothing else ever refreshes it. Without a fetch of
 * its own, "losing admin rights takes effect without a new sign-in" would be
 * aspirational — there is no refresh for it to hang on.
 *
 * Fail closed in every unknown: request failed, field absent, not signed in.
 *
 * Spec: specs/active/2026-08-26-stars-rate-and-admin-guard/ (US3).
 */
const AdminGuard = ({ children }: { children: JSX.Element }) => {
  const dispatch = useDispatch();
  const isUserLoggedIn = useSelector(
    (state: RootState) => state.userData.isUserLoggedIn
  );
  const apiKey = useSelector((state: RootState) => state.userData.apiKey);
  const isAdmin = useSelector((state: RootState) => state.userData.isAdmin);

  // Distinct from `isAdmin` being undefined: this tracks OUR fetch, so a store
  // value left over from page load never short-circuits the re-check.
  const [resolved, setResolved] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const resolveAdmin = async () => {
      if (!isUserLoggedIn || !apiKey) {
        // 🔴 "No key in the store" is NOT the same as "anonymous". On a hard
        // load of an admin URL the store starts empty and `useAuthInitialization`
        // (App.tsx) restores the session asynchronously — resolving here would
        // paint the not-found page first and the panel a moment later, which
        // reads to an admin as a plain 404 on their own panel.
        //
        // localStorage answers it synchronously: a stored key means a session is
        // still being restored, so keep waiting. The effect re-runs when the key
        // lands in the store, because `apiKey` is a dependency.
        let restoring = false;
        try {
          restoring = Boolean(localStorage.getItem('api_key'));
        } catch {
          // Storage can throw (private mode, blocked cookies). Treat an
          // unreadable store as "nothing to restore" and fail closed.
          restoring = false;
        }
        if (!restoring && !cancelled) setResolved(true);
        return;
      }
      try {
        const account = await getAccountDetails(apiKey);
        if (cancelled) return;
        dispatch(
          setIsAdmin(account.status === 'success' && account.data.is_admin)
        );
      } catch {
        // A failed lookup is not "keep whatever we had" — it is "not an admin".
        if (!cancelled) dispatch(setIsAdmin(false));
      } finally {
        if (!cancelled) setResolved(true);
      }
    };

    resolveAdmin();
    return () => {
      cancelled = true;
    };
  }, [dispatch, isUserLoggedIn, apiKey]);

  if (!resolved) {
    // Render nothing admin-shaped until we know. Rendering the shell and then
    // removing it leaks both the layout and the fact that the address exists.
    return (
      <Center minH="60vh">
        <Spinner size="lg" thickness="3px" speed="0.7s" />
      </Center>
    );
  }

  if (!isUserLoggedIn || isAdmin !== true) {
    return <NotFoundPage />;
  }

  return children;
};

export default AdminGuard;
